import React, { useState, useRef, useEffect, useCallback } from 'react';

const DEFAULT_DEBOUNCE_MS = 500;

/**
 * A controlled input/textarea that buffers value locally to prevent
 * cursor jumping when the parent uses async state updates (like saving to localStorage).
 * 
 * Features debounce save - automatically saves after user stops typing.
 * 
 * @param {string} value - Initial/external value
 * @param {function} onSync - Called on blur and after debounce delay to persist the value
 * @param {function} onChange - Optional callback for real-time updates (e.g., validation)
 * @param {number} debounceMs - Debounce delay in ms (default: 500ms)
 * @param {'input'|'textarea'} as - Element type to render
 * @param {object} props - All other props passed to the underlying element
 */
export function BufferedInput({
  value: externalValue,
  onSync,
  onChange,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  as = 'input',
  ...props
}) {
  const [localValue, setLocalValue] = useState(externalValue ?? '');
  const Component = as;
  const debounceTimeoutRef = useRef(null);
  const lastSyncedValueRef = useRef(externalValue ?? '');

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Debounced sync function
  const debouncedSync = useCallback((value) => {
    // Clear any pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new debounce timeout
    debounceTimeoutRef.current = setTimeout(() => {
      // Only sync if value has actually changed since last sync
      if (value !== lastSyncedValueRef.current) {
        lastSyncedValueRef.current = value;
        onSync?.(value);
      }
    }, debounceMs);
  }, [onSync, debounceMs]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange?.(newValue);
    // Trigger debounced save
    debouncedSync(newValue);
  };

  const handleBlur = () => {
    // Clear pending debounce and sync immediately on blur
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    // Only sync if value has changed since last sync
    if (localValue !== lastSyncedValueRef.current) {
      lastSyncedValueRef.current = localValue;
      onSync?.(localValue);
    }
  };

  return (
    <Component
      {...props}
      value={localValue}
      onInput={handleChange}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}

/**
 * Convenience wrapper for textarea
 */
export function BufferedTextarea(props) {
  return <BufferedInput as="textarea" {...props} />;
}

export default BufferedInput;
